import { faAngleRight as rightIcon } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Spinner } from '@kcftech/design-system';
import { Checkbox } from 'components/forms/Checkbox';
import { RadioButtonInput } from 'components/forms/RadioButton';
import * as React from 'react';
import { memo, useEffect, useMemo, useRef } from 'react';
import { DataTreeNodes, DataTreeNodesProps } from 'shared/data_tree/DataTreeNodes';
import {
    ChildNodes,
    ExpansionButton,
    ExpansionSpacer,
    NodeContent,
    NodeExpansion,
    NodeLabel,
    RenderedNodeLabel,
    SelectionInputWrapper,
    SpinnerIcon,
} from 'shared/data_tree/styled';
import { TreeDragDrop } from 'shared/data_tree/TreeDragDrop';
import { TreeNode } from 'shared/data_tree/types';
import { pointerDownClickHandler } from 'utils/pointer_events';

export type DataTreeNodeProps<TData> = Readonly<
    Omit<DataTreeNodesProps<TData>, 'nodes'> & {
        node: TreeNode<TData>;
    }
>;

function DataTreeNode<TData>({
    treeId,
    node,
    parentNodeId,
    renderNode,
    expandable,
    selectable,
    movable,
    scrollToNodeId,
    isTreeLoading = false,
    onNodeRef,
}: DataTreeNodeProps<TData>) {
    const hasScrolledRef = useRef<boolean>(false);
    const nodeRef = useRef<HTMLDivElement>(null);
    const isRoot = parentNodeId === undefined;
    const onLoadChildrenRef = useRef(expandable?.onLoadChildren);
    onLoadChildrenRef.current = expandable?.onLoadChildren;

    const isExpanded = useMemo(
        () => (expandable ? expandable.isExpanded(node) : true),
        [expandable, node],
    );

    const isExpandable = useMemo(
        () =>
            (expandable ? (expandable.canExpand?.(node) ?? true) : false) &&
            (node.children === undefined || node.children.length > 0),
        [expandable, node],
    );

    const isLoading = useMemo(
        () => (expandable?.isLoading ? expandable.isLoading(node) : false),
        [expandable, node],
    );

    const isSelected = useMemo(
        () => selectable?.isSelected(node) ?? false,
        [selectable, node],
    );

    const isSelectable = useMemo(
        () => (selectable ? (selectable.canSelect?.(node) ?? true) : false),
        [node, selectable],
    );

    const isSelectionDisabled = useMemo(
        () => selectable?.isDisabled?.(node) ?? false,
        [node, selectable],
    );

    const isIndeterminate = useMemo(
        () =>
            (selectable?.selectionStyle === 'checkbox' &&
                selectable.isIndeterminate?.(node)) ??
            false,
        [node, selectable],
    );

    const isMovable = useMemo(
        () => (movable ? (movable.canBeMoved?.(node) ?? true) : false),
        [movable, node],
    );

    const onExpansionToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (isExpandable && expandable?.onChange) {
            expandable.onChange({ node, isExpanded: !isExpanded, event });
            event.preventDefault();
            event.stopPropagation();
        }
    };

    const onSelectionToggle = (
        event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    ) => {
        event.preventDefault();
        event.stopPropagation();

        if (isSelectable && !isSelectionDisabled && selectable?.onChange) {
            selectable.onChange({ node, isSelected: !isSelected, event });
        }
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        if (isSelectionDisabled) {
            return;
        }
        if (isSelectable && (event.code === 'Space' || event.code === 'Enter')) {
            onSelectionToggle(event);
        }
    };

    const renderedNode = useMemo(
        () =>
            renderNode({
                isSelectable,
                isSelected,
                isExpandable,
                isExpanded,
                isMovable,
                node,
            }),
        [isExpandable, isExpanded, isMovable, isSelectable, isSelected, node, renderNode],
    );

    const showExpansionButton = expandable && isExpandable;
    const showExpansionSpacer =
        expandable && !isExpandable && (!isRoot || expandable.indentNonExpandableRoots);

    useEffect(() => {
        if (
            onLoadChildrenRef.current &&
            isExpanded &&
            node.children === undefined &&
            !isLoading
        ) {
            onLoadChildrenRef.current(node);
        }
    }, [isExpanded, isLoading, node]);

    useEffect(() => {
        if (
            scrollToNodeId === node.id &&
            nodeRef.current &&
            !isTreeLoading &&
            !hasScrolledRef.current
        ) {
            nodeRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            hasScrolledRef.current = true;
        }
    }, [node.id, scrollToNodeId, isTreeLoading]);

    if (renderedNode === 'subtree-not-rendered') {
        return <></>;
    }

    return (
        <>
            <TreeDragDrop<TData>
                treeId={treeId}
                node={node}
                parentNodeId={parentNodeId}
                movable={movable}
                isMovable={isMovable}
            >
                <NodeContent ref={nodeRef} className="data-tree-node">
                    <NodeExpansion>
                        {isLoading && (
                            <SpinnerIcon>
                                <Spinner size="1x" />
                            </SpinnerIcon>
                        )}
                        {showExpansionButton && !isLoading && (
                            <ExpansionButton
                                type="button"
                                {...pointerDownClickHandler(onExpansionToggle)}
                                aria-label="Toggle expanded"
                            >
                                <FontAwesomeIcon
                                    className={`transition-transform ${
                                        isExpanded ? 'rotate-90' : ''
                                    }`}
                                    icon={rightIcon}
                                />
                            </ExpansionButton>
                        )}
                        {showExpansionSpacer && <ExpansionSpacer />}
                    </NodeExpansion>

                    <NodeLabel
                        role="button"
                        tabIndex={isSelectable ? 0 : -1}
                        $isSelectable={isSelectable}
                        $isDisabled={isSelectionDisabled}
                        onClick={onSelectionToggle}
                        onKeyDown={onKeyDown}
                        id={node.id}
                        className="tree-node-btn"
                        ref={(element) => onNodeRef?.(node.id, element)}
                    >
                        {isSelectable && selectable?.selectionStyle === 'checkbox' && (
                            <SelectionInputWrapper>
                                <Checkbox
                                    checked={
                                        isSelected || (isIndeterminate && 'indeterminate')
                                    }
                                    disabled={isSelectionDisabled}
                                    tabIndex={-1}
                                    readOnly
                                    style={{ pointerEvents: 'none' }}
                                />
                            </SelectionInputWrapper>
                        )}
                        {isSelectable && selectable?.selectionStyle === 'radio' && (
                            <SelectionInputWrapper>
                                <RadioButtonInput
                                    type="radio"
                                    checked={isSelected}
                                    disabled={isSelectionDisabled}
                                    tabIndex={-1}
                                    readOnly
                                    style={{ pointerEvents: 'none' }}
                                />
                            </SelectionInputWrapper>
                        )}
                        <RenderedNodeLabel>{renderedNode}</RenderedNodeLabel>
                    </NodeLabel>
                </NodeContent>
            </TreeDragDrop>
            {node.children && isExpanded && (
                <ChildNodes $isExpanded={isExpanded}>
                    <DataTreeNodes<TData>
                        treeId={treeId}
                        nodes={node.children}
                        parentNodeId={node.id}
                        renderNode={renderNode}
                        expandable={expandable}
                        selectable={selectable}
                        movable={movable}
                        scrollToNodeId={scrollToNodeId}
                        isTreeLoading={isTreeLoading}
                        onNodeRef={onNodeRef}
                    />
                </ChildNodes>
            )}
        </>
    );
}

const memoDataTreeNode = memo(DataTreeNode) as typeof DataTreeNode;

export { memoDataTreeNode as DataTreeNode };
